/* eslint-disable jsx-a11y/anchor-is-valid */
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classnames from 'classnames';
import { PaginationProps, usePagination } from 'react-instantsearch-hooks-web';
import { PaginationDataLayerObject, gtmDatalayerPush } from 'lib/util/gtm-datalayer';
import { useCallback } from 'react';
import { useFastDeepEqualMemo } from 'lib/react';
import { useRouter } from 'next/router';

export function handlePaginationClick(action: string, pageNumber: number, resultCount: number) {
  const gtmData: PaginationDataLayerObject = {
    event: 'e_pagination',
    contentType: document.title,
    pageNumber,
    resultCount,
  };
  if (action !== 'page') {
    gtmData.direction = action;
  }
  gtmDatalayerPush(gtmData);
}

const pagenationLink = `pagination-link`;

function Page({
  index,
  pages,
  currentRefinement,
  createURL,
  refine,
  nbHits,
  alwaysRender,
}: {
  index: number;
  pages: number[];
  currentRefinement: number;
  createURL: (page: number) => string;
  refine: (page: number) => void;
  nbHits: number;
  alwaysRender?: boolean;
}) {
  return (
    <>
      {alwaysRender || pages.indexOf(index) === -1 ? (
        <li
          className={classnames(
            'pagination-item',
            'text-blue-navy',
            'text-body',
            `${currentRefinement === index ? 'selected' : ''}`
          )}
        >
          <a
            className={classnames(pagenationLink)}
            href={createURL(index)}
            onClick={(event) => {
              event.preventDefault();
              refine(index);
              handlePaginationClick('page', index + 1, nbHits);
            }}
          >
            {index + 1}
          </a>
        </li>
      ) : (
        <></>
      )}
    </>
  );
}

export function CustomPaginationWithSearch(props: PaginationProps) {
  const pagination = usePagination(props);
  const { pages, currentRefinement, nbPages, isFirstPage, isLastPage, refine, nbHits } = pagination;
  const router = useRouter();

  const createUrl = useCallback(
    (page: number) => {
      const path = !!router?.isReady ? router.asPath : '';
      return `${path?.split('?')[0]}?page=${page + 1}&${path.split('?')[1] || ''}`;
    },
    [router?.asPath, router?.isReady]
  );

  const {
    previousIcon,
    endIcon,
    startTwoPagination,
    endTwoPagination,
    firstDot,
    lastDot,
    pageElements,
  } = useFastDeepEqualMemo(() => {
    if (nbPages < 2) {
      return {
        previousIcon: <></>,
        endIcon: <></>,
        startTwoPagination: <></>,
        endTwoPagination: <></>,
        firstDot: <></>,
        lastDot: <></>,
      };
    }
    const previousIconElement = (): React.JSX.Element => {
      return (
        <>
          {isFirstPage ? (
            <li>
              <a
                href="#"
                className={classnames('pagination-prev', 'pagination-disabled')}
                aria-label="Previous page (disabled)"
              >
                <span />
              </a>
            </li>
          ) : (
            <li>
              <a
                className={classnames('pagination-prev', pagenationLink)}
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  handlePaginationClick('previous', currentRefinement, nbHits);
                  refine(currentRefinement - 1);
                }}
                aria-label="Previous page"
              >
                <span />
              </a>
            </li>
          )}
        </>
      );
    };
    const endIconElement = (): React.JSX.Element => {
      return (
        <>
          {isLastPage ? (
            <li>
              <a
                href="#"
                className={classnames('pagination-next', 'pagination-disabled')}
                aria-label="Next page (disabled)"
              >
                <span />
              </a>
            </li>
          ) : (
            <li>
              <a
                className={classnames('pagination-next', pagenationLink)}
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  refine(currentRefinement + 1);
                  handlePaginationClick('next', currentRefinement + 2, nbHits);
                }}
                aria-label="Next page"
              >
                <span />
              </a>
            </li>
          )}
        </>
      );
    };
    const startTwoPaginationElement = (): React.JSX.Element => {
      return (
        <>
          <Page
            index={0}
            pages={pages}
            currentRefinement={currentRefinement}
            refine={refine}
            nbHits={nbHits}
            createURL={createUrl}
          />
          <Page
            index={1}
            pages={pages}
            currentRefinement={currentRefinement}
            refine={refine}
            nbHits={nbHits}
            createURL={createUrl}
          />
        </>
      );
    };
    const endTwoPaginationElement = (): React.JSX.Element => {
      return (
        <>
          <Page
            index={nbPages - 2}
            pages={pages}
            currentRefinement={currentRefinement}
            refine={refine}
            nbHits={nbHits}
            createURL={createUrl}
          />
          <Page
            index={nbPages - 1}
            pages={pages}
            currentRefinement={currentRefinement}
            refine={refine}
            nbHits={nbHits}
            createURL={createUrl}
          />
        </>
      );
    };

    const firstDotElement = (): React.JSX.Element => {
      if (pages.length && pages[0] > 2) {
        return <li className={classnames('pagination-dots', 'text-blue-navy')} />;
      }
      return <></>;
    };

    const lastDotElement = (): React.JSX.Element => {
      if (pages.length && pages[pages.length - 1] < nbPages - 3) {
        return <li className={classnames('pagination-dots', 'text-blue-navy')} />;
      }
      return <></>;
    };

    const renderPages = (): React.JSX.Element => {
      if (nbPages < 2) {
        return <></>;
      }
      return (
        <>
          {pages.map(
            (page): JSX.Element => (
              <Page
                key={page}
                index={page}
                pages={pages}
                currentRefinement={currentRefinement}
                refine={refine}
                nbHits={nbHits}
                createURL={createUrl}
                alwaysRender
              />
            )
          )}
        </>
      );
    };

    return {
      previousIcon: previousIconElement(),
      endIcon: endIconElement(),
      startTwoPagination: startTwoPaginationElement(),
      endTwoPagination: endTwoPaginationElement(),
      firstDot: firstDotElement(),
      lastDot: lastDotElement(),
      pageElements: renderPages(),
    };
  }, [isFirstPage, currentRefinement, refine, pages, nbPages, nbHits, createUrl]);

  return (
    <>
      {nbPages > 1 ? (
        <ul className={classnames('pagination-list')}>
          <li data-component="previousIcon" />
          {previousIcon}
          <li data-component="startTwoPagination" />
          {startTwoPagination}
          <li data-component="firstDot" />
          {firstDot}
          <li data-component="pages" />
          {pageElements}
          <li data-component="lastDot" />
          {lastDot}
          <li data-component="endTwoPagination" />
          {endTwoPagination}
          <li data-component="endIcon" />
          {endIcon}
        </ul>
      ) : (
        <></>
      )}
    </>
  );
}

export function CustomPagination(props: PaginationProps) {
  const { sitecoreContext } = useSitecoreContext();
  const pageEditing = sitecoreContext.pageEditing === true;
  if (pageEditing) {
    return (
      <>
        <ul className={classnames('pagination-list')}>
          <li>1</li>
        </ul>
      </>
    );
  }
  return <CustomPaginationWithSearch {...props} />;
}
